import type {
  DocumentDownloadUrlResponse,
  DocumentFormat,
} from '~/api/k2/typedefs.autogen';
import type { ServiceRequestConfig } from '~/services/createService';

import { fetchFileFromPresignedUrl } from '~/common/utils/presignedUrl';
import { getFileExtension } from '~/eds/src/utils/getFileExtension';
import { FileMimeType } from '~/enums/FileMimeType';
import { SFDT_CONVERTIBLE_FORMATS } from '~/features/document-viewer/constants';
import { k2 } from '~/services/serviceInstances';

import { recordOpaqueContentTypeMetric } from './recordOpaqueContentTypeMetric';

export type DownloadDocumentMode = 'preview' | 'download';

export type DownloadDocumentRequest = {
  documentId: string;
  documentVersion?: number | null;
  mode?: DownloadDocumentMode;
  fileFormat?: string | null;
  targetFormat?: 'pdf';
};

const OPAQUE_MIME_TYPES = new Set([
  '',
  'binary/octet-stream',
  'application/octet-stream',
]);

function isPdfDocument(fileFormat: string | null | undefined, name: string) {
  const extension = fileFormat || getFileExtension(name).slice(1);
  return extension.toLowerCase() === 'pdf';
}

function resolveDownloadFormat(
  mode: DownloadDocumentMode | undefined,
  fileFormat: string | null | undefined,
  targetFormat?: DownloadDocumentRequest['targetFormat'],
): DocumentFormat {
  if (targetFormat === 'pdf') {
    return 'pdf';
  }

  if (
    mode === 'preview' &&
    fileFormat &&
    SFDT_CONVERTIBLE_FORMATS.has(fileFormat)
  ) {
    return 'sfdt';
  }

  return 'original';
}

export const downloadDocument = async (
  parentPath: string,
  request: DownloadDocumentRequest,
  options?: ServiceRequestConfig,
): Promise<File> => {
  const format = resolveDownloadFormat(
    request.mode,
    request.fileFormat,
    request.targetFormat,
  );

  const urlResponse: DocumentDownloadUrlResponse = await k2.get(
    `${parentPath}/documents/${request.documentId}/download-request`,
    {
      ...options,
      params: {
        ...(request.documentVersion != null
          ? { document_version: request.documentVersion }
          : {}),
        format,
      },
    },
  );

  const { download_url, name } = urlResponse.data.attributes;

  if (!download_url) {
    throw new Error('Document download URL is not available');
  }

  const file = await fetchFileFromPresignedUrl({
    url: download_url,
    fileName: name,
  });

  if (format === 'sfdt') {
    return new File([file], file.name, { type: FileMimeType.Sfdt });
  }

  // Pilot's multipart S3 copy drops Content-Type above 8MB (EKP-57361), so large
  // PDFs arrive as binary/octet-stream and the viewer can't route them.
  if (
    OPAQUE_MIME_TYPES.has(file.type) &&
    isPdfDocument(request.fileFormat, name)
  ) {
    recordOpaqueContentTypeMetric(file.type);
    return new File([file], file.name, { type: FileMimeType.Pdf });
  }

  return file;
};
